/**
 * NavigationControls.tsx
 *
 * Stateless navigation bar rendered above the weekly calendar grid.
 *
 * Displays:
 * - A **Previous week** button (chevron-left) on the left.
 * - The current **week label** (e.g. "5 June – 11 June 2025") centred.
 * - A **"Return to Today"** button below the week label, shown only when the
 *   user is not viewing the current week.
 * - A **Next week** button (chevron-right) on the right.
 *
 * Both navigation buttons can be hidden by passing `hideNavButtons={true}`.
 * This is used when the mini-month calendar's month-selector overlay is open,
 * to prevent accidental week navigation while the overlay is visible.
 *
 * All interactive elements include `aria-label` attributes for screen-reader
 * accessibility.
 */

'use client';

import React from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

/** Props accepted by `NavigationControls`. */
interface Props {
  /** Formatted string describing the visible week, e.g. `"5 June – 11 June 2025"`. */
  weekLabel: string;
  /** Called when the user clicks the Previous week button. */
  onPrev: () => void;
  /** Called when the user clicks the Next week button. */
  onNext: () => void;
  /** Called when the user clicks the "Return to Today" button. */
  onToday: () => void;
  /** When `true`, the "Return to Today" button is hidden (user is already on the current week). */
  isAtCurrentWeek: boolean;
  /**
   * When `true`, both Prev and Next buttons are replaced by empty spacers.
   * Used while the month-selector overlay is open to prevent accidental navigation.
   */
  hideNavButtons?: boolean;
}

export default function NavigationControls({ weekLabel, onPrev, onNext, onToday, isAtCurrentWeek, hideNavButtons }: Props) {
  return (
    <nav
      className="flex items-center justify-between gap-3 py-2"
      aria-label="Week navigation"
    >
      {/* Prev button */}
      {!hideNavButtons && (
        <button
          onClick={onPrev}
          className="nav-btn-arcane rounded-lg p-2 sm:p-2.5 flex items-center gap-1.5 focus:outline-none focus:ring-1 focus:ring-primary/50"
          aria-label="Previous week"
        >
          <ChevronLeft size={16} className="text-primary" />
          <span className="hidden sm:inline font-display text-xs text-muted-foreground tracking-wider">Prev</span>
        </button>
      )}
      {hideNavButtons && <div className="w-10" />}

      {/* Center: week label + today button */}
      <div className="flex-1 flex flex-col items-center gap-1">
        <div className="flex items-center gap-2">
          <span className="text-primary/30 text-xs" aria-hidden>✦</span>
          <h2 className="font-display text-xs sm:text-sm text-foreground/80 tracking-wider text-center">
            {weekLabel}
          </h2>
          <span className="text-primary/30 text-xs" aria-hidden>✦</span>
        </div>
        {!isAtCurrentWeek && (
          <button
            onClick={onToday}
            className="flex items-center gap-1 font-display text-[10px] text-primary/70 hover:text-primary
              transition-colors duration-200 tracking-wider uppercase focus:outline-none focus:ring-1 focus:ring-primary/40 rounded px-1"
            aria-label="Return to current week"
          >
            <Calendar size={10} />
            Return to Today
          </button>
        )}
      </div>

      {/* Next button */}
      {!hideNavButtons && (
        <button
          onClick={onNext}
          className="nav-btn-arcane rounded-lg p-2 sm:p-2.5 flex items-center gap-1.5 focus:outline-none focus:ring-1 focus:ring-primary/50"
          aria-label="Next week"
        >
          <span className="hidden sm:inline font-display text-xs text-muted-foreground tracking-wider">Next</span>
          <ChevronRight size={16} className="text-primary" />
        </button>
      )}
      {hideNavButtons && <div className="w-10" />}
    </nav>
  );
}